/**
 * Analise da esteira: credito, subscricao e juridico da operacao.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import type { Money } from './comum';

import { api } from '../client';

export interface CreditLimit {
  limit: Money;
  used: Money;
  available: Money;
  rate_bps: number;
}

export type CreditStatus = 'em_analise' | 'aprovado' | 'reprovado' | 'pendente';

export interface CreditAnalysis {
  id: string;
  operation_id: string;
  status: CreditStatus;
  traditional: CreditLimit;
  judicial: CreditLimit;
  valid_until: string | null;
  expired: boolean;
  opinion: string;
  decline_reason: string;
  analyst_label: string;
  decided_at: string | null;
  updated_at: string;
}

export interface CreditLimitInput {
  limit?: Money;
  used?: Money;
  rate_bps?: number;
}

export interface CreditInput {
  traditional?: CreditLimitInput;
  judicial?: CreditLimitInput;
  valid_until?: string | null;
  opinion?: string;
}

export interface ChecklistItem {
  id: string;
  label: string;
  checked: boolean;
}

export interface UnderwritingReview {
  id: string;
  operation_id: string;
  status: 'pendente' | 'aprovada' | 'reprovada';
  notes: string;
  decline_reason: string;
  checklist: ChecklistItem[];
  pending_items: string[];
  reviewer_label: string;
  decided_at: string | null;
  updated_at: string;
}

export type LegalStatus = 'nao_iniciado' | 'em_analise' | 'pendencia' | 'aprovado';

export interface LegalReview {
  id: string;
  operation_id: string;
  status: LegalStatus;
  opinion: string;
  reviewer_label: string;
  decided_at: string | null;
  updated_at: string;
}

/**
 * Quadro ausente significa que a pessoa nao tem a permissao daquela area, e nao
 * que o parecer nao exista. A tela mostra o que veio.
 */
export interface AnalysisBundle {
  credit: CreditAnalysis | null;
  underwriting: UnderwritingReview | null;
  legal: LegalReview | null;
}

export const analysis = {
  get: (operationId: string, signal?: AbortSignal) =>
    api.get<AnalysisBundle>(`/api/v1/operations/${operationId}/analysis`, signal),
  saveCredit: (operationId: string, input: CreditInput) =>
    api.patch<CreditAnalysis>(
      `/api/v1/operations/${operationId}/analysis/credit`,
      input,
    ),
  // justification: justificativa da aprovacao, exigida quando a politica dos pareceres
  // da empresa manda (aprovacao contra a recomendacao do motor, C-ALC).
  decideCredit: (
    operationId: string,
    status: CreditStatus,
    reason?: string,
    justification?: string,
  ) =>
    api.post<CreditAnalysis>(
      `/api/v1/operations/${operationId}/analysis/credit/decision`,
      {
        status,
        ...(reason ? { reason } : {}),
        ...(justification ? { justification } : {}),
      },
    ),
  saveUnderwriting: (
    operationId: string,
    input: { notes?: string; checklist?: { id: string; checked: boolean }[] },
  ) =>
    api.patch<UnderwritingReview>(
      `/api/v1/operations/${operationId}/analysis/underwriting`,
      input,
    ),
  decideUnderwriting: (
    operationId: string,
    status: 'aprovada' | 'reprovada',
    reason?: string,
    justification?: string,
  ) =>
    api.post<UnderwritingReview>(
      `/api/v1/operations/${operationId}/analysis/underwriting/decision`,
      {
        status,
        ...(reason ? { reason } : {}),
        ...(justification ? { justification } : {}),
      },
    ),
  decideLegal: (operationId: string, status: LegalStatus, opinion: string) =>
    api.post<LegalReview>(`/api/v1/operations/${operationId}/analysis/legal/decision`, {
      status,
      opinion,
    }),
};
